import React from 'react';

import { button, dataGrid, icon, tab, tabPanel, tabs } from '@harmony/enablers/react';
import { AuthWorkspace } from '@harmony/pcportal-shell-library';

import {
  ActionTypes,
  HealthStatus,
  ServiceHealthIds,
  ServiceIncidentStatus,
  WorkspaceCategoryPc,
} from '../../../Common/Constants';
import { ErrorTypes } from '../../../Common/Constants/ErrorTypes.Constants';
import { ServiceIncident } from '../../../Services/Services.Primary/HttpService/Models/ServiceIncident';
import TelemetryService from '../../../Services/Services.Primary/Telemetry/TelemetryService';
import { getHarmonyScope } from '../../../Services/Services.Secondary';
import LocalizationService from '../../../Services/Services.Secondary/Localization/LocalizationService';
import { getDateTimeInternationalFormat, getLanguageFromBrowser } from '../../../Utilities/Helpers';
import { getComponentWhenReady, getDummyRows, getRowsDataAndHTML } from '../../../Utilities/Helpers/Harmony.Helper';
import { ServiceHealthHelper } from '../Dependency Helpers/ServiceHealthHelper';
import Table, { HarmonyTableColumn, HarmonyTableRow } from '../Generic/Table';
import HealthHistoryTableRow from './HealthHistoryTableRow';
import { Incident, ServiceStatus } from './Models/ServiceHealthTables';
import { columnsHealthHistory, columnsServiceStatus } from './ServiceHealthColumnStyling';
import StatusTableRow from './StatusTableRow';

const Button = getHarmonyScope().forReact(button);
const DataGrid = getHarmonyScope().forReact(dataGrid);
const Icon = getHarmonyScope().forReact(icon);
const Tab = getHarmonyScope().forReact(tab);
const TabPanel = getHarmonyScope().forReact(tabPanel);
const Tabs = getHarmonyScope().forReact(tabs);

const resx = LocalizationService.getInstance();

const lastUpdatedText = resx.getLocalizedResource('CurrentStatus_text');
const serviceStatusText = resx.getLocalizedResource('ServiceStatus_text');
const healthHistoryText = resx.getLocalizedResource('HealthHistory_text');
const refreshText = resx.getLocalizedResource('Refresh_text');
const statusTableDescription = resx.getLocalizedResource('StatusTableDescription_text');
const healthHistoryTableDescription = resx.getLocalizedResource('HealthHistoryTableDescription_text');
const noOutagesMessageText = resx.getLocalizedResource('NoIncidentsReported_text');
const noServiceHealthIncidentsReported = resx.getLocalizedResource('NoServiceHealthIncidentsReported_text');

enum TabsIds {
  serviceHealth = 'service-health',
  healthHistory = 'health-history',
}

type ServiceHealthContainerState = {
  statusList: Map<string, ServiceStatus> | null;
  incidents: Incident[];
  loadingStatusList: boolean;
  loadingIncidentList: boolean;
  rowsServiceStatus: any[];
  htmlRowsServiceStatus: any[];
  rowsHealthHistory: HarmonyTableRow[];
  htmlRowsHealthHistory: any[];
  activeTab: TabsIds;
  authWorkspaceList: AuthWorkspace[];
};

export default class ServiceHealthContainer extends React.Component<any, ServiceHealthContainerState> {
  private serviceHealthHelper: ServiceHealthHelper;
  private serviceHealthStatusResponse: Map<string, ServiceStatus> | undefined;
  private telemetryService: TelemetryService;

  constructor(props: any) {
    super(props);
    this.state = {
      statusList: null,
      incidents: [],
      loadingStatusList: true, // Default value should be true. // When we fetch the contents we can update the state.
      loadingIncidentList: true,
      rowsServiceStatus: [],
      htmlRowsServiceStatus: [],
      rowsHealthHistory: [],
      htmlRowsHealthHistory: [],
      activeTab: TabsIds.serviceHealth,
      authWorkspaceList: [],
    };

    this.serviceHealthHelper = ServiceHealthHelper.getInstance();
    this.telemetryService = TelemetryService.getInstance();
  }

  componentDidMount() {
    this.fetchStatusList().then(() => {
      if (this.props.isPublic) {
        this.setState({ loadingIncidentList: false });
      } else {
        this.fetchIncidentList();
      }
    });
  }

  private fetchStatusList = async () => {
    this.setState({ loadingStatusList: true });

    const initTime = performance.now();
    // We will start with data fetching before checking for the Flight because that is what we always need.
    // and also it is ensuring that status list is requested because client context has already been requested.
    await this.updateHealthStatusList();

    // Indicates the Time when the service health status list call completes.
    const statusListTime = performance.now();

    // We are adding logs to understand when the flight check was complete.
    const flightCheckTime = performance.now();

    if (this.serviceHealthStatusResponse) {
      try {
        if (this.props.isPublic) {
          await this.updateAllStatusList();
        } else {
          await this.updateAuthStatusList();
        }

        const serviceHealthListCompletionTime = performance.now();
        const eventLog = {
          name: ActionTypes.serviceHealthStatusList,
          statusListDuration: statusListTime - initTime,
          fligthCheckDuration: flightCheckTime - statusListTime,
          durationSincePageLoad: performance.now(),
          duration: serviceHealthListCompletionTime - initTime,
        };
        this.telemetryService.traceEvent(ActionTypes.serviceHealthStatusList, eventLog);
      } catch (error) {
        this.setState({ rowsServiceStatus: [], htmlRowsServiceStatus: [] });

        const errorLog = {
          component: 'ServiceHealthContainer',
          function: 'fetchStatusList',
          error: JSON.stringify(error),
        };
        this.telemetryService.traceError(ErrorTypes.serviceHealthStatusFailed, errorLog);
      }
    } else {
      this.setState({ rowsServiceStatus: [], htmlRowsServiceStatus: [] });
    }

    this.setState({ loadingStatusList: false });
  };

  private updateHealthStatusList = async () => {
    const serviceHealthStatus = await this.serviceHealthHelper.getServiceHealth();
    if (serviceHealthStatus) {
      this.serviceHealthStatusResponse = serviceHealthStatus;
    }
  };

  private updateAuthStatusList = async () => {
    if (this.serviceHealthStatusResponse) {
      try {
        const authWorkspaceList = await this.serviceHealthHelper.getAuthWorkspace();
        if (authWorkspaceList && authWorkspaceList?.length >= 1) {
          //filter statusList by AuthWorkspaces
          const authStatusHealthMap = new Map(
            [...this.serviceHealthStatusResponse].filter(([_, serviceHealthStatus]) =>
              authWorkspaceList.find(
                authElement => authElement.workspaceName.toLowerCase() === serviceHealthStatus.workspace.toLowerCase()
              )
            )
          );

          //Special check for customer workspace exist in the Authworkspaces
          // if customer workspace exist we add te APIandIntegration.
          let isCustomerWsExist = Array.from(authStatusHealthMap.values())
            .map(x => x.serviceId.toLowerCase())
            .includes(ServiceHealthIds.Customer.toLowerCase());

          //workspace category map.
          const WorkspaceCategoryPCMap = new Map(
            [...this.serviceHealthStatusResponse].filter(
              ([_, serviceHealthStatus]) =>
                (isCustomerWsExist &&
                  serviceHealthStatus.workspace.toLowerCase() ===
                    WorkspaceCategoryPc.APIandIntegration.toLocaleLowerCase()) ||
                serviceHealthStatus.workspace.toLowerCase() ===
                  WorkspaceCategoryPc.AccountSettings.toLocaleLowerCase() ||
                serviceHealthStatus.workspace.toLowerCase() === WorkspaceCategoryPc.ActionCenter.toLowerCase() ||
                serviceHealthStatus.workspace.toLowerCase() === WorkspaceCategoryPc.Enrollment.toLowerCase()
            )
          );

          //spread maps
          const authWorkspaceServiceHealthList = new Map([...authStatusHealthMap, ...WorkspaceCategoryPCMap]);
          const list = authWorkspaceServiceHealthList
            ? this.serviceHealthHelper.getSortedList(Array.from(authWorkspaceServiceHealthList.values()))
            : [];
          const rows = this.getServiceStatusRows(list);
          this.setState({
            statusList: authWorkspaceServiceHealthList,
            rowsServiceStatus: rows.data,
            htmlRowsServiceStatus: rows.html,
          });
        } else {
          // Adding a Fallback if there is a failure in using the connector
          this.updateAllStatusList();
        }
      } catch (error) {
        // Adding the Fallback if there is a failure or issues with retrieving data from connector.
        this.updateAllStatusList();

        const errorLog = {
          component: 'ServiceHealthContainer',
          function: 'updateAuthStatusList',
          error: error,
        };
        this.telemetryService.traceError(ErrorTypes.authWorkspaceListFailed, errorLog);
      }
    }
  };

  // when flight:PFXserviceHealthAuthWorkspaces is turned-off
  private updateAllStatusList = async () => {
    if (this.serviceHealthStatusResponse) {
      const list = this.serviceHealthStatusResponse
        ? this.serviceHealthHelper.getSortedList(Array.from(this.serviceHealthStatusResponse.values()))
        : [];
      const rows = this.getServiceStatusRows(list);
      this.setState({
        statusList: this.serviceHealthStatusResponse,
        loadingStatusList: false,
        rowsServiceStatus: rows.data,
        htmlRowsServiceStatus: rows.html,
      });
    }
  };

  private getLastUpdateDiv = () => {
    const locale = getLanguageFromBrowser();
    return (
      <div className="spacer-2x-large-bottom">
        <span className='lastUpdatedFields'>
          {lastUpdatedText}
        </span>
        <span className="lastUpdatedFields">
          {getDateTimeInternationalFormat(new Date(), locale)}{' '}
        </span>
      </div>
    );
  };

  private fetchIncidentList = async () => {
    this.setState({ loadingIncidentList: true });
    const incidentPromise = this.serviceHealthHelper.getServiceIncidents();
    await incidentPromise
      .then(response => {
        let incidents = [];

        const currentDateTime = new Date();
        const last30DaysInISOFormat = new Date(currentDateTime.setDate(currentDateTime.getDate() - 30)).toISOString();

        const resolvedIncidents = response
          .filter(
            item =>
              item &&
              item.incidentStartDateTime &&
              item.status === ServiceIncidentStatus.Resolved &&
              item.incidentStartDateTime > last30DaysInISOFormat
          )
          .filter(item => this.state.statusList && item.serviceId && this.state.statusList.has(item.serviceId))
          .sort((IncidentX: ServiceIncident, IncidentY: ServiceIncident) =>
            IncidentY.incidentStartDateTime && IncidentX.incidentStartDateTime
              ? IncidentY.incidentStartDateTime.localeCompare(IncidentX.incidentStartDateTime)
              : -1
          );

        for (let incident of resolvedIncidents) {
          let i: Incident = {
            date: incident.incidentStartDateTime ?? '',
            description: incident.description ?? '',
            workspace: this.state.statusList?.get(incident.serviceId ?? '')?.workspace ?? '',
            serviceName: this.state.statusList?.get(incident.serviceId ?? '')?.name ?? '',
            resolution: incident.resolution,
            rootCause: incident.rootCause,
          };
          incidents.push(i);
        }

        const rows = this.getHealthHistoryRows(incidents);
        this.setState({
          incidents: incidents,
          loadingIncidentList: false,
          rowsHealthHistory: rows.data,
          htmlRowsHealthHistory: rows.html,
        });
      })
      .catch(() => {
        this.setState({ loadingIncidentList: false, rowsHealthHistory: [], htmlRowsHealthHistory: [] });
      });
  };

  private getTable = (loading: boolean, columns: HarmonyTableColumn[], table: JSX.Element) => {
    return getComponentWhenReady(
      loading,
      <DataGrid loading={true} rows={getDummyRows()} columns={columns}></DataGrid>,
      table
    );
  };

  private getServiceStatusHarmonyRow = (serviceHealth: ServiceStatus, index: number): HarmonyTableRow => {
    let description =
      serviceHealth.status === HealthStatus.Good.Code || !serviceHealth.statusDescription
        ? ''
        : serviceHealth.statusDescription;
    return {
      id: index,
      cells: {
        name: serviceHealth.name,
        status: `${serviceHealth.status}`,
        statusDescription: description,
        workspace: serviceHealth.workspace,
      },
    };
  };

  private getServiceStatusHTMLRow = (serviceHealth: ServiceStatus, index: number): JSX.Element => {
    return <StatusTableRow serviceHealth={serviceHealth} id={index} key={index} serviceHealthPageType={this.props.serviceHealthPageType}/>;
  };

  private getServiceStatusRows = (serviceStatus: ServiceStatus[]) => {
    return getRowsDataAndHTML<ServiceStatus>(
      serviceStatus,
      this.getServiceStatusHarmonyRow,
      this.getServiceStatusHTMLRow
    );
  };

  private getHealthHistoryHarmonyRow = (incident: Incident, index: number): HarmonyTableRow => {
    return {
      id: index,
      cells: {
        date: incident.date,
        service: incident.serviceName,
        description: incident.description,
        workspace: incident.workspace,
      },
    };
  };

  private getHealthHistoryHTMLRow = (incident: Incident, index: number): JSX.Element => {
    return <HealthHistoryTableRow incident={incident} id={index} key={index} />;
  };

  private getHealthHistoryRows = (incidents: Incident[]) => {
    return getRowsDataAndHTML<Incident>(incidents, this.getHealthHistoryHarmonyRow, this.getHealthHistoryHTMLRow);
  };

  private getRefreshButton = (onClick: () => void) => {
    return (
      <Button appearance="command" onClick={onClick}>
        {refreshText}
        <Icon slot="start" name="refresh"></Icon>
      </Button>
    );
  };

  public render() {
    return (
      <div>
        <div>
          <Tabs size="large" active-id={TabsIds.serviceHealth}>
            <Tab id={TabsIds.serviceHealth}>{serviceStatusText}</Tab>
            {!this.props.isPublic && <Tab id={TabsIds.healthHistory}>{healthHistoryText}</Tab>}
            <TabPanel>
              <>
                <div>
                  <p>{statusTableDescription}</p>
                </div>
                {this.getLastUpdateDiv()}
                {this.getRefreshButton(this.fetchStatusList)}
                {this.getTable(
                  this.state.loadingStatusList,
                  columnsServiceStatus,
                  <Table
                    id={'service-health-table'}
                    sortBy={'status'}
                    columns={columnsServiceStatus}
                    rows={this.state.rowsServiceStatus}
                    loading={this.state.loadingStatusList}
                    htmlRowsContent={this.state.htmlRowsServiceStatus}
                    noRowsMessage={noServiceHealthIncidentsReported}
                  />
                )}
              </>
            </TabPanel>
            {!this.props.isPublic && (
              <TabPanel>
                <>
                  <div>
                    <p>{healthHistoryTableDescription}</p>
                  </div>
                  {this.getLastUpdateDiv()}
                  {this.getRefreshButton(this.fetchIncidentList)}
                  {this.getTable(
                    this.state.loadingIncidentList,
                    columnsHealthHistory,
                    <Table
                      id={'health-history-table'}
                      sortBy={'date'}
                      columns={columnsHealthHistory}
                      rows={this.state.rowsHealthHistory}
                      loading={this.state.loadingIncidentList}
                      htmlRowsContent={this.state.htmlRowsHealthHistory}
                      noRowsMessage={noOutagesMessageText}
                    />
                  )}
                </>
              </TabPanel>
            )}
          </Tabs>
        </div>
      </div>
    );
  }
}
